/**
 * Vehicle Management Hooks
 * React Query hooks for vehicle operations using Edge Functions
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../services/api';
import { useLocations } from './useLocations';
import type { Vehicle, VehicleCreate, VehicleUpdate, VehicleWithLocation } from '../types/vrp';

// Get vehicles for a specific project with scenario context
export const useVehicles = (projectId: number, scenarioId?: number, options?: {
  dataset_name?: string;
  dataset_version?: number;
  dataset_id?: number;
  enabled?: boolean;
  include_all_project_data?: boolean;
}) => {
  return useQuery({
    queryKey: ['vehicles', projectId, scenarioId, options],
    enabled: (options?.enabled !== false && projectId > 0) && !!projectId,
    queryFn: async () => {
      console.log('🚗 [useVehicles] Fetching vehicles for project:', projectId, 'scenario:', scenarioId);
      const result = await apiClient.getVehicles(projectId, {
        scenario_id: scenarioId,
        dataset_name: options?.dataset_name,
        dataset_version: options?.dataset_version,
        dataset_id: options?.dataset_id,
        include_all_project_data: options?.include_all_project_data
      });
      console.log('🔍 [useVehicles] Raw API response:', result);
      
      // Handle Edge Function response format: {success: true, data: [...], meta: {...}}
      if (result && result.success && Array.isArray(result.data)) {
        console.log('✅ [useVehicles] Returning vehicles array:', result.data.length);
        return result.data;
      }
      
      // Fallback: if it's already an array (direct response)
      if (Array.isArray(result)) {
        console.log('✅ [useVehicles] Direct array response:', result.length);
        return result;
      }
      
      console.warn('⚠️ [useVehicles] Unexpected response format, returning empty array');
      return [];
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (renamed from cacheTime in React Query v5)
  });
};

// Hook to get a single vehicle WITH LOCATION DETAILS (following useJob pattern)
export const useVehicle = (vehicleId: number, projectId: number) => {
  // Location dependency for single vehicle
  const { data: locations, isLoading: locationsLoading } = useLocations(projectId);
  
  return useQuery({
    queryKey: ['vehicle', vehicleId, projectId],
    queryFn: () => apiClient.getVehicle(vehicleId, projectId),
    enabled: !!vehicleId && !!projectId && !locationsLoading && !!locations,
    staleTime: 5 * 60 * 1000,
  });
};

// Hook to get available locations for vehicle selection (following useAvailableLocationsForJobs pattern)
export const useAvailableLocationsForVehicles = (projectId: number, scenarioId?: number, options?: {
  dataset_name?: string;
  dataset_version?: number;
  dataset_id?: number;
}) => {
  const { data: locations, isLoading, error } = useLocations(projectId, scenarioId, { 
    limit: 100,
    dataset_name: options?.dataset_name,
    dataset_version: options?.dataset_version,
    dataset_id: options?.dataset_id
  });
  
  return {
    data: locations || [],
    isLoading,
    error,
    // Helper to find location by ID
    getLocationById: (locationId: number) => 
      locations?.find(loc => loc.id === locationId),
    // Helper to get location options for dropdowns
    getLocationOptions: () => 
      locations?.map(loc => ({
        value: loc.id,
        label: `${loc.name}${loc.address ? ` - ${loc.address}` : ''}`,
        location: loc
      })) || []
  };
};

// Create a new vehicle with location master integration
export const useCreateVehicle = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (vehicleData: VehicleCreate) => {
      // Validate required context before API call
      if (!vehicleData.scenario_id) {
        throw new Error('scenario_id is required for vehicle creation - ensure scenario context is provided');
      }
      if (!vehicleData.dataset_id) {
        throw new Error('dataset_id is required for vehicle creation - ensure dataset context is provided');
      }
      if (!vehicleData.project_id) {
        throw new Error('project_id is required for vehicle creation');
      }
      
      console.log('🚗 Creating vehicle with context:', {
        project_id: vehicleData.project_id,
        scenario_id: vehicleData.scenario_id,
        dataset_id: vehicleData.dataset_id
      });
      
      return apiClient.createVehicle(vehicleData);
    },
    onSuccess: (newVehicle, variables) => {
      // Invalidate vehicles query for the specific project and scenario
      queryClient.invalidateQueries({ queryKey: ['vehicles', variables.project_id, variables.scenario_id] });
      queryClient.invalidateQueries({ queryKey: ['vehicles', variables.project_id] });
      // Invalidate all vehicles queries to ensure consistency
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
      // Invalidate locations cache if locations were updated
      if (variables.start_location_id || variables.end_location_id) {
        queryClient.invalidateQueries({ queryKey: ['locations', variables.project_id] });
      }
      
      console.log('✅ Vehicle created successfully with context:', {
        vehicleId: newVehicle?.id || 'unknown',
        scenario_id: variables.scenario_id,
        dataset_id: variables.dataset_id
      });
    },
    onError: (error) => {
      console.error('❌ Vehicle creation failed:', error);
    }
  });
};

// Update an existing vehicle with location references
export const useUpdateVehicle = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ vehicleId, vehicleData, projectId }: {
      vehicleId: number;
      vehicleData: VehicleUpdate;
      projectId: number;
    }) => apiClient.updateVehicle(vehicleId, vehicleData, projectId),
    onSuccess: (updatedVehicle, variables) => {
      // Invalidate specific vehicle query
      queryClient.invalidateQueries({ queryKey: ['vehicle', variables.vehicleId] });
      // Invalidate vehicles list for the project
      queryClient.invalidateQueries({ queryKey: ['vehicles', variables.projectId] });
      // Invalidate all vehicles queries to ensure consistency
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
      // Invalidate locations cache if locations were updated
      if (variables.vehicleData.start_location_id || variables.vehicleData.end_location_id) {
        queryClient.invalidateQueries({ queryKey: ['locations', variables.projectId] });
      }
    },
    onError: (error) => {
      console.error('Vehicle update failed:', error);
    }
  });
};

// Delete a vehicle
export const useDeleteVehicle = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ vehicleId, projectId }: { vehicleId: number; projectId: number }) => 
      apiClient.deleteVehicle(vehicleId, projectId),
    onSuccess: (_, variables) => {
      // Remove the specific vehicle from cache
      queryClient.removeQueries({ queryKey: ['vehicle', variables.vehicleId] });
      // Invalidate vehicles list for the project
      queryClient.invalidateQueries({ queryKey: ['vehicles', variables.projectId] });
      // Invalidate all vehicles queries to ensure consistency
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
    },
    onError: (error) => {
      console.error('Vehicle deletion failed:', error);
    }
  });
};

// Bulk operations for vehicle management
export const useBulkCreateVehicles = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ vehicles, projectId, scenarioId, datasetId }: { 
      vehicles: VehicleCreate[]; 
      projectId: number; 
      scenarioId: number; 
      datasetId: number; 
    }) => {
      // Validate required context before API call
      if (!scenarioId) {
        throw new Error('scenario_id is required for bulk vehicle creation - ensure scenario context is provided');
      }
      if (!datasetId) {
        throw new Error('dataset_id is required for bulk vehicle creation - ensure dataset context is provided');
      }
      if (!projectId) {
        throw new Error('project_id is required for bulk vehicle creation');
      }
      
      console.log('🚗 Bulk creating vehicles with context:', {
        project_id: projectId,
        scenario_id: scenarioId,
        dataset_id: datasetId,
        vehicle_count: vehicles.length
      });
      
      return apiClient.bulkCreateVehicles(vehicles, projectId, scenarioId, datasetId);
    },
    onSuccess: (_, variables) => {
      // Invalidate all vehicles queries for the project and scenario
      queryClient.invalidateQueries({ queryKey: ['vehicles', variables.projectId, variables.scenarioId] });
      queryClient.invalidateQueries({ queryKey: ['vehicles', variables.projectId] });
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
      
      console.log('✅ Bulk vehicle creation successful with context:', {
        project_id: variables.projectId,
        scenario_id: variables.scenarioId,
        dataset_id: variables.datasetId,
        vehicle_count: variables.vehicles.length
      });
    },
    onError: (error) => {
      console.error('❌ Bulk vehicle creation failed:', error);
    }
  });
};

// Export types for use in components
export type { VehicleWithLocation, VehicleCreate, VehicleUpdate };

